import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  ArrowLeft, Pin, Lock, Clock, MessageCircle, Send, 
  MoreVertical, Trash2, Edit, Link2, Image as ImageIcon, HelpCircle, X
} from "lucide-react";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ImageUpload from "@/components/ImageUpload";
import { parseSimpleMarkdown } from "@/utils/formatContent";
import { getImageUrl } from "@/utils/imageUtils";
import ForumRichContent from "@/components/ForumRichContent";
import { createEntityLink } from "@/components/EntityEmbed";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const REACTION_EMOJIS = ["🐟", "🐠", "🦈", "🐡", "🐙", "🦑", "🐚", "🦀", "🐳", "🐬", "🦭", "🪸", "🤿", "👍", "❤️", "😂", "😮", "🔥"];

// Formatting Cheatsheet Component
const FormattingHelp = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold text-lg text-gray-900">Formatting Cheatsheet</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Text Formatting</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>**bold**</code> → <strong>bold</strong></p>
              <p><code>*italic*</code> → <em>italic</em></p>
              <p><code>`code`</code> → <code className="bg-gray-200 px-1 rounded">code</code></p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Colors</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>{'{color:red}text{/color}'}</code> → <span className="text-red-500">text</span></p>
              <p className="text-gray-500 text-xs mt-1">Colors: red, blue, green, orange, purple, pink, cyan, teal, yellow, gray, navy, lime, gold</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Links</h4>
            <div className="bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>[link text](https://url)</code></p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Images (Inline)</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>![alt](#0)</code> → 1st uploaded image</p>
              <p><code>![alt](#1|center)</code> → 2nd image, centered</p>
              <p><code>![alt](#2|right)</code> → 3rd image, right-aligned</p>
              <p className="text-gray-500 text-xs mt-1">Positions: left (default), center, right</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Lists</h4>
            <div className="bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>- Item 1</code></p>
              <p><code>- Item 2</code></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ThreadDetail = () => {
  const navigate = useNavigate();
  const { threadId } = useParams();
  const [user, setUser] = useState(null);
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showReactions, setShowReactions] = useState(null);
  const [showOptions, setShowOptions] = useState(null);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role === "dive_center") {
        navigate("/");
        return;
      }
      setUser(parsedUser);
    } else {
      navigate("/account");
    }
  }, [navigate]);

  useEffect(() => {
    if (threadId) {
      fetchThread();
      fetchReplies();
    }
  }, [threadId]);

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchThread = async () => {
    try {
      const response = await axios.get(`${API}/forum/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setThread(response.data);
    } catch (err) {
      console.error("Failed to fetch thread:", err);
      navigate("/community/forum");
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      const response = await axios.get(`${API}/forum/threads/${threadId}/replies`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setReplies(response.data.replies);
    } catch (err) {
      console.error("Failed to fetch replies:", err);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || thread?.is_locked) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/forum/threads/${threadId}/replies`,
        { content: replyContent, images: replyImages, mentions: [], linked_threads: [] },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setReplyContent("");
      setReplyImages([]);
      fetchReplies();
      fetchThread(); // Refresh reply count
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReaction = async (targetType, targetId, emoji) => {
    try {
      const endpoint = targetType === "thread" 
        ? `${API}/forum/threads/${targetId}/react`
        : `${API}/forum/replies/${targetId}/react`;
      
      const response = await axios.post(
        endpoint,
        { emoji },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (targetType === "thread") {
        setThread(prev => ({ ...prev, reactions: response.data.reactions }));
      } else {
        setReplies(prev => prev.map(r => 
          r.id === targetId ? { ...r, reactions: response.data.reactions } : r
        ));
      }
      setShowReactions(null);
    } catch (err) {
      console.error("Failed to react:", err);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!confirm("Are you sure you want to delete this reply?")) return;
    
    try {
      await axios.delete(`${API}/forum/replies/${replyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      fetchReplies();
      fetchThread();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete reply");
    }
  };

  const handleDeleteThread = async () => {
    if (!confirm("Are you sure you want to delete this thread? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/forum/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      navigate("/community/forum");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete thread");
    }
  };

  const handlePinThread = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/forum/threads/${threadId}/pin`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setThread(prev => ({ ...prev, is_pinned: response.data.is_pinned }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to pin thread");
    }
  };

  const handleLockThread = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/forum/threads/${threadId}/lock`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setThread(prev => ({ ...prev, is_locked: response.data.is_locked }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to lock thread");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const renderReactions = (reactions, targetType, targetId) => {
    const hasReactions = Object.keys(reactions || {}).length > 0;
    
    return (
      <div className="flex items-center gap-2 relative">
        {hasReactions && (
          <div className="flex gap-1">
            {Object.entries(reactions).map(([emoji, users]) => (
              <button
                key={emoji}
                onClick={() => handleReaction(targetType, targetId, emoji)}
                className={`px-2 py-1 rounded-full text-sm flex items-center gap-1 transition-colors ${
                  users.includes(user?.id)
                    ? "bg-cyan-100 border border-cyan-300"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {emoji} <span className="text-xs text-gray-600">{users.length}</span>
              </button>
            ))}
          </div>
        )}
        
        <div className="relative">
          <button
            onClick={() => setShowReactions(showReactions === targetId ? null : targetId)}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            +
          </button>
          
          {showReactions === targetId && (
            <div className="absolute bottom-full left-0 mb-2 p-2 bg-white rounded-lg shadow-lg border flex flex-wrap gap-1 w-64 z-10">
              {REACTION_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(targetType, targetId, emoji)}
                  className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading || !thread) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title={`${thread.title} - George Dives Forum`} />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-24 pb-4 bg-white border-b">
        <div className="container mx-auto px-4 max-w-4xl">
          <button
            onClick={() => navigate("/community/forum")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Forum
          </button>
        </div>
      </section>

      {/* Thread Content */}
      <section className="py-6">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className={thread.is_pinned ? "border-l-4 border-l-cyan-500" : ""}>
            <CardContent className="p-6">
              {/* Thread Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {thread.author_profile_pic ? (
                    <img 
                      src={getImageUrl(thread.author_profile_pic)} 
                      alt={thread.author_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                      {thread.author_name?.charAt(0) || "?"}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{thread.author_name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(thread.created_at)}
                    </p>
                  </div>
                </div>
                
                {/* Thread Actions */}
                <div className="flex items-center gap-2">
                  {thread.is_pinned && (
                    <span className="flex items-center gap-1 text-cyan-600 text-sm">
                      <Pin className="w-4 h-4" /> Pinned
                    </span>
                  )}
                  {thread.is_locked && (
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <Lock className="w-4 h-4" /> Locked
                    </span>
                  )}
                  
                  {(thread.author_id === user?.id || user?.role === "admin") && (
                    <div className="relative">
                      <button
                        onClick={() => setShowOptions(showOptions === "thread" ? null : "thread")}
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>
                      
                      {showOptions === "thread" && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-10">
                          <button
                            onClick={() => {
                              const link = createEntityLink("thread", thread.id);
                              navigator.clipboard.writeText(link);
                              toast.success("Thread link copied! Paste it in chat or other posts.");
                              setShowOptions(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            data-testid="copy-thread-link-btn"
                          >
                            <Link2 className="w-4 h-4" />
                            Copy Link
                          </button>
                          {user?.role === "admin" && (
                            <>
                              <button
                                onClick={handlePinThread}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Pin className="w-4 h-4" />
                                {thread.is_pinned ? "Unpin" : "Pin"} Thread
                              </button>
                              <button
                                onClick={handleLockThread}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Lock className="w-4 h-4" />
                                {thread.is_locked ? "Unlock" : "Lock"} Thread
                              </button>
                            </>
                          )}
                          <button
                            onClick={handleDeleteThread}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Thread
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Title & Tags */}
              <h1 className="text-2xl font-bold text-gray-900 mb-3">{thread.title}</h1>
              
              {thread.tags.length > 0 && (
                <div className="flex gap-2 mb-4">
                  {thread.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Content - with markdown + entity embeds */}
              <div className="prose prose-gray max-w-none mb-4">
                <ForumRichContent
                  content={thread.content}
                  images={thread.images}
                  className="text-gray-800"
                />
              </div>

              {/* Images */}
              {thread.images?.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {thread.images.map((img, i) => (
                    <img 
                      key={i} 
                      src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img} 
                      alt="" 
                      className="rounded-lg max-h-64 object-cover cursor-pointer hover:opacity-90"
                      onClick={() => window.open(img.startsWith('/api') ? `${BACKEND_URL}${img}` : img, '_blank')}
                    />
                  ))}
                </div>
              )}

              {/* Reactions */}
              <div className="pt-4 border-t">
                {renderReactions(thread.reactions, "thread", thread.id)}
              </div>
            </CardContent>
          </Card>

          {/* Replies */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              {replies.length} {replies.length === 1 ? "Reply" : "Replies"}
            </h2>

            <div className="space-y-4">
              {replies.map(reply => (
                <Card key={reply.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {reply.author_profile_pic ? (
                        <img 
                          src={getImageUrl(reply.author_profile_pic)} 
                          alt={reply.author_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold">
                          {reply.author_name?.charAt(0) || "?"}
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <span className="font-semibold text-gray-900">{reply.author_name}</span>
                            <span className="text-sm text-gray-500 ml-2">
                              {formatDate(reply.created_at)}
                            </span>
                          </div>
                          
                          {(reply.author_id === user?.id || user?.role === "admin") && (
                            <button
                              onClick={() => handleDeleteReply(reply.id)}
                              className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Reply content with markdown + entity embeds */}
                        <ForumRichContent
                          content={reply.content}
                          images={reply.images}
                          className="text-gray-700 mb-3"
                        />
                        
                        {/* Reply Images */}
                        {reply.images?.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {reply.images.map((img, i) => (
                              <img 
                                key={i} 
                                src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img} 
                                alt="" 
                                className="rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90"
                                onClick={() => window.open(img.startsWith('/api') ? `${BACKEND_URL}${img}` : img, '_blank')}
                              />
                            ))}
                          </div>
                        )}
                        
                        {renderReactions(reply.reactions, "reply", reply.id)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Reply Form */}
            {!thread.is_locked ? (
              <form onSubmit={handleSubmitReply} className="mt-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Write a reply</span>
                      <button
                        type="button"
                        onClick={() => setShowFormattingHelp(true)}
                        className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                        Formatting help
                      </button>
                    </div>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write a reply... (supports **bold**, *italic*, {color:blue}colors{/color})"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none mb-3"
                      data-testid="reply-input"
                    />
                    
                    {/* Image Upload for Reply */}
                    <div className="mb-3">
                      <ImageUpload
                        uploadType="forum"
                        multiple={true}
                        currentImages={replyImages}
                        onImagesUpdated={setReplyImages}
                      />
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        className="bg-cyan-500 hover:bg-cyan-600"
                        disabled={!replyContent.trim() || submitting}
                        data-testid="submit-reply-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? "Posting..." : "Post Reply"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </form>
            ) : (
              <Card className="mt-6 bg-gray-50">
                <CardContent className="p-4 text-center text-gray-500">
                  <Lock className="w-5 h-5 mx-auto mb-2" />
                  This thread is locked. No new replies can be posted.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Formatting Help Modal */}
      <FormattingHelp isOpen={showFormattingHelp} onClose={() => setShowFormattingHelp(false)} />

      <Footer />
    </div>
  );
};

export default ThreadDetail;
